import {
  deleteBasketSuccess,
  switchBasket,
  switchBasketFailure,
  switchBasketSuccess,
} from '@epicuro-next/state/basket-state';
import { createReducer, on } from '@ngrx/store';
import {
  BasketManagerInfo,
  BasketSearchResult,
  Maybe,
  UserBaskets,
  UserBasketsSearchResult,
} from 'graphql-api/baskets-api-interfaces';

import {
  clearPaginationData,
  closeBasketManager,
  openBasketManager,
  searchBaskets,
  searchBasketsFailure,
  searchBasketsSuccess,
  searchGroupedBaskets,
  searchGroupedBasketsFailure,
  searchGroupedBasketsSuccess,
} from '../actions/basket-switch.actions';

export const basketSwitchFeatureKey = 'switch-basket';

export interface State {
  basketSearchData: BasketSearchResult | null;
  userBasketSearchData?: UserBasketsSearchResult | null;
  loading: boolean;
  allLoaded: boolean;
  page: number;
  usersAmount: number;
  basketsAmount: number;
  basketManagerOpened: boolean;
  /** Boolean to check if switching the basket */
  switching: boolean;
}

export const initialState: State = {
  basketSearchData: null,
  userBasketSearchData: null,
  loading: true,
  allLoaded: false,
  page: 0,
  usersAmount: 25,
  basketsAmount: 128,
  basketManagerOpened: false,
  switching: false,
};

export const reducer = createReducer(
  initialState,
  on(
    searchGroupedBaskets,
    searchBaskets,
    (state): State => ({ ...state, loading: true }),
  ),
  on(
    searchGroupedBasketsFailure,
    searchBasketsFailure,
    (state): State => ({
      ...state,
      loading: false,
    }),
  ),
  on(
    searchBasketsSuccess,
    (state, { data }): State => {
      const newSearchData = state.basketSearchData
        ? {
            totalResultCount: data?.totalResultCount,
            baskets: (state.basketSearchData
              ?.baskets as BasketManagerInfo[]).concat(
              data?.baskets as BasketManagerInfo[],
            ),
          }
        : data;

      const allLoaded = Boolean(
        newSearchData?.totalResultCount &&
          newSearchData?.baskets?.length &&
          newSearchData?.baskets.length === newSearchData.totalResultCount,
      );

      return {
        ...state,
        basketSearchData: newSearchData,
        loading: false,
        page: state.page + state.basketsAmount,
        allLoaded,
      };
    },
  ),
  on(
    searchGroupedBasketsSuccess,
    (state, { data }): State => {
      const newSearchData = state.userBasketSearchData
        ? {
            items: (state.userBasketSearchData?.items as UserBaskets[]).concat(
              data?.items as UserBaskets[],
            ),
            total: data?.total,
          }
        : data;

      const allLoaded = Boolean(
        newSearchData?.items?.length &&
          newSearchData?.total &&
          newSearchData?.items.length === newSearchData.total,
      );

      return {
        ...state,
        userBasketSearchData: newSearchData,
        loading: false,
        page: state.page + state.usersAmount,
        allLoaded,
      };
    },
  ),

  on(
    clearPaginationData,
    (state): State => ({
      ...state,
      page: 0,
      basketSearchData: null,
      userBasketSearchData: null,
      allLoaded: false,
    }),
  ),
  on(
    openBasketManager,
    (state): State => ({
      ...state,
      basketManagerOpened: true,
    }),
  ),
  on(
    closeBasketManager,
    (state): State => ({
      ...state,
      basketManagerOpened: false,
    }),
  ),
  on(
    deleteBasketSuccess,
    (state, { basketId: deletedBasketId }): State => {
      const { userBasketSearchData } = state;
      if (!userBasketSearchData) return state;

      const items = removeUserBasketById(
        userBasketSearchData?.items ?? [],
        deletedBasketId,
      );

      return {
        ...state,
        userBasketSearchData: { ...state.userBasketSearchData, items },
      };
    },
  ),
  on(
    deleteBasketSuccess,
    (state, { basketId: deletedBasketId }): State => {
      const { basketSearchData } = state;
      if (!basketSearchData) return state;

      const baskets = removeBasketById(
        basketSearchData?.baskets ?? [],
        deletedBasketId,
      );

      return {
        ...state,
        basketSearchData: {
          ...state.basketSearchData,
          baskets,
        },
      };
    },
  ),
  // switching
  on(
    switchBasket,
    (state): State => ({
      ...state,
      switching: true,
    }),
  ),
  on(
    switchBasketSuccess,
    switchBasketFailure,
    (state): State => ({
      ...state,
      switching: false,
    }),
  ),
);

function removeUserBasketById(
  items: Maybe<UserBaskets>[],
  deletedBasketId: string,
): UserBaskets[] {
  return (
    items.map((item) => ({
      ...item,
      baskets: item?.baskets?.filter(
        (basketItem) => basketItem?.id !== deletedBasketId,
      ),
    })) || []
  );
}

function removeBasketById(
  items: Maybe<BasketManagerInfo>[],
  deletedBasketId: string,
): Maybe<BasketManagerInfo>[] {
  return items.filter((item) => item?.basketId !== deletedBasketId);
}
